import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, RefreshCw, X, ExternalLink } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";

// Self-serve data-source connect card for the owner portal Home.
// Renders NOTHING for firm-managed clients (their firm handles connections).
//
// Business rules (2026-08):
// - SOLE TRADERS: only see a Prosaic invite CTA. NZ Taxation pays for their
//   Prosaic subscription; the client just needs to accept the org invite via
//   the fixed invite URL. No Xero option, no API key entry.
// - COMPANIES: see the historical dual Xero/Prosaic UX (self-serve API key).
export default function ConnectDataSourceCard() {
    const { tenant, activeClient } = useAuth();
    const isOwnerMode = tenant?.mode === "owner";
    const isSoleTrader = activeClient?.entity_type === "sole_trader";

    const PROSAIC_INVITE_URL = "https://app.prosaic.works/invite/nz-taxation";

    const [xero, setXero] = useState({ loading: true, connections: [], configured: false });
    const [prosaic, setProsaic] = useState({ loading: true, connections: [], configured: false });
    const [busy, setBusy] = useState(null);   // 'xero' | 'prosaic' | 'sync:<id>' | null
    const [showProsaicForm, setShowProsaicForm] = useState(false);
    const [prosaicKey, setProsaicKey] = useState("");

    useEffect(() => {
        if (!isOwnerMode) return;
        // Surface the callback result if the user just came back from Xero.
        try {
            const url = new URL(window.location.href);
            const ok = url.searchParams.get("xero_ok");
            const err = url.searchParams.get("xero_error");
            if (ok) {
                toast.success(`Xero connected — ${ok} organisation${ok === "1" ? "" : "s"} ready.`);
                url.searchParams.delete("xero_ok");
                window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
            } else if (err) {
                toast.error(`Xero connection failed: ${err}`);
                url.searchParams.delete("xero_error");
                window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
            }
        } catch { /* ignore */ }
        loadAll();
    }, [isOwnerMode]);

    async function loadAll() {
        setXero((s) => ({ ...s, loading: true }));
        setProsaic((s) => ({ ...s, loading: true }));
        try {
            const [x, p] = await Promise.all([
                api.get("/xero/status").catch(() => ({ data: { configured: false, connections: [] } })),
                api.get("/portal/prosaic/status").catch(() => ({ data: { configured: false, connections: [] } })),
            ]);
            setXero({ loading: false, configured: !!x.data.configured, connections: x.data.connections || [] });
            setProsaic({ loading: false, configured: !!p.data.configured, connections: p.data.connections || [] });
        } catch {
            setXero({ loading: false, configured: false, connections: [] });
            setProsaic({ loading: false, configured: false, connections: [] });
        }
    }

    async function startXero() {
        setBusy("xero");
        try {
            const { data } = await api.get("/xero/connect");
            window.location.href = data.authorize_url;
        } catch (e) {
            toast.error(formatApiError(e));
            setBusy(null);
        }
    }

    async function connectProsaic(e) {
        e?.preventDefault();
        if (!prosaicKey.trim()) return;
        setBusy("prosaic");
        try {
            const { data } = await api.post("/portal/prosaic/connect", { api_key: prosaicKey.trim() });
            toast.success(`Prosaic connected — ${data.mapped?.length || 0} entit${data.mapped?.length === 1 ? "y" : "ies"} ready.`);
            setProsaicKey("");
            setShowProsaicForm(false);
            await loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(null);
        }
    }

    async function syncXero(connectionId) {
        setBusy(`sync-x-${connectionId}`);
        try {
            await api.post(`/xero/connections/${connectionId}/sync`);
            toast.success("Xero sync complete.");
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    }

    async function syncProsaic(connectionId) {
        setBusy(`sync-p-${connectionId}`);
        try {
            await api.post(`/prosaic/entities/${connectionId}/sync`).catch(async () => {
                // Owners don't have firm-side /prosaic access. If that 403s,
                // the daily scheduler will still catch this connection — but
                // we can offer the /portal/prosaic/status refresh in the meantime.
                await loadAll();
            });
            toast.success("Prosaic sync queued.");
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    }

    async function disconnectProsaic() {
        if (!window.confirm("Disconnect Prosaic? Your saved API key will be removed.")) return;
        try {
            await api.delete("/portal/prosaic/connection");
            toast.success("Prosaic disconnected.");
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    if (!isOwnerMode) return null;

    if (xero.loading || prosaic.loading) {
        return (
            <div className="rounded-2xl border border-[#E4E4E7] bg-white p-5" data-testid="owner-data-card-loading">
                <div className="flex items-center gap-2 text-sm text-[#5E636E]">
                    <Loader2 className="h-4 w-4 animate-spin" /> Checking your data sources…
                </div>
            </div>
        );
    }

    const xeroOn = xero.connections.length > 0;
    const prosaicOn = prosaic.connections.length > 0;
    const bothOff = !xeroOn && !prosaicOn;

    // -------- Sole trader flow (Prosaic invite only, no Xero) --------
    if (isSoleTrader) {
        if (prosaicOn) {
            return (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="owner-data-connected-card">
                    <div className="inline-flex items-center gap-2 text-sm font-medium text-emerald-800" data-testid="owner-data-label">
                        <CheckCircle2 className="h-4 w-4" /> Prosaic connected
                    </div>
                    <ul className="mt-2 space-y-1 text-sm text-[#0F1115]" data-testid="owner-prosaic-connections">
                        {prosaic.connections.map((c) => (
                            <li key={c.id} className="flex items-center gap-2" data-testid={`owner-prosaic-connection-${c.id}`}>
                                <span className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Prosaic:</span>
                                <span className="font-medium">{c.prosaic_entity_name || c.prosaic_entity_id}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-3 text-[12px] text-[#5E636E]">
                        NZ Taxation Limited manages your Prosaic subscription — included in your monthly fee.
                    </p>
                </div>
            );
        }
        return (
            <div
                className="rounded-2xl border border-[#E4E4E7] bg-gradient-to-br from-[#FFFFFF] to-[#F5F4EF] p-5 sm:p-6"
                data-testid="owner-data-connect-card"
            >
                <div className="co-label text-[#0E3B2E]">Bring your own numbers</div>
                <h3 className="mt-1 font-display text-lg sm:text-xl font-semibold tracking-tight">
                    Connect your bank via Prosaic
                </h3>
                <p className="mt-2 text-sm text-[#5E636E] leading-relaxed">
                    NZ Taxation Limited already pays for your Prosaic subscription — it&apos;s included in your
                    monthly fee. Accept the org invite below to link your bank account securely; your
                    transactions flow straight into your ClientOps snapshot.
                </p>
                <div className="mt-4">
                    <a
                        href={PROSAIC_INVITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-[#0E3B2E] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#0B1F17] transition-colors"
                        data-testid="sole-trader-prosaic-invite-btn"
                    >
                        Accept Prosaic invite <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                </div>
                <p className="mt-3 text-[11px] text-[#9CA0A8]">
                    Opens Prosaic in a new tab. Sign in (or create your Prosaic account with the same email)
                    and accept the NZ Taxation invite — you&apos;re done.
                </p>
            </div>
        );
    }

    // -------- Company flow (existing Xero + Prosaic self-serve UX) --------
    // Header label — reflects reality across both sources.
    const label = xeroOn && prosaicOn
        ? "Xero + Prosaic connected"
        : xeroOn
            ? "Xero connected"
            : prosaicOn
                ? "Prosaic connected"
                : null;

    // Neither connected → both connect paths side-by-side.
    if (bothOff) {
        return (
            <div
                className="rounded-2xl border border-[#E4E4E7] bg-gradient-to-br from-[#FFFFFF] to-[#F5F4EF] p-5 sm:p-6"
                data-testid="owner-data-connect-card"
            >
                <div className="co-label text-[#0E3B2E]">Bring your own numbers</div>
                <h3 className="mt-1 font-display text-lg sm:text-xl font-semibold tracking-tight">
                    Connect Xero or Prosaic to fill your snapshot
                </h3>
                <p className="mt-2 text-sm text-[#5E636E] leading-relaxed">
                    Your data stays yours. We only read what we need for your snapshot,
                    deductibility answers, and freedom-date maths — never write, never share.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {xero.configured && (
                        <button
                            type="button"
                            onClick={startXero}
                            disabled={busy === "xero"}
                            className="inline-flex items-center gap-2 rounded-full bg-[#0E3B2E] px-4 py-2 text-sm font-medium text-white hover:bg-[#0B1F17] transition-colors disabled:opacity-60"
                            data-testid="owner-xero-connect-btn"
                        >
                            {busy === "xero" ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <>Connect Xero <ArrowRight className="h-3.5 w-3.5" /></>}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowProsaicForm((v) => !v)}
                        className="inline-flex items-center gap-2 rounded-full border border-[#0E3B2E] px-4 py-2 text-sm font-medium text-[#0E3B2E] hover:bg-[#0E3B2E]/5 transition-colors"
                        data-testid="owner-prosaic-connect-btn"
                    >
                        Connect Prosaic <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                </div>
                {showProsaicForm && (
                    <form onSubmit={connectProsaic} className="mt-4 rounded-xl bg-white border border-[#E4E4E7] p-4 space-y-3" data-testid="owner-prosaic-form">
                        <label className="block text-[12px] text-[#5E636E]">
                            Paste your Prosaic API key
                            <input
                                type="password"
                                autoComplete="off"
                                value={prosaicKey}
                                onChange={(e) => setProsaicKey(e.target.value)}
                                placeholder="prosaic_live_…"
                                className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-sm text-[#0F1115] focus:outline-none focus:ring-2 focus:ring-[#0E3B2E]/20"
                                data-testid="owner-prosaic-key-input"
                            />
                            <span className="mt-1 block text-[11px] text-[#5E636E]">
                                From Prosaic → Settings → API keys. We store it encrypted-at-rest and only ever use it to read your reports.
                            </span>
                        </label>
                        <div className="flex items-center gap-2">
                            <button
                                type="submit"
                                disabled={busy === "prosaic" || prosaicKey.trim().length < 8}
                                className="inline-flex items-center gap-1.5 rounded-full bg-[#0E3B2E] px-4 py-2 text-sm font-medium text-white hover:bg-[#0B1F17] disabled:opacity-50"
                                data-testid="owner-prosaic-submit"
                            >
                                {busy === "prosaic" ? <><Loader2 className="h-4 w-4 animate-spin" /> Connecting…</> : "Save & connect"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowProsaicForm(false); setProsaicKey(""); }}
                                className="text-[13px] text-[#5E636E] hover:text-[#0F1115]"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
                <p className="mt-3 text-[11px] text-[#9CA0A8]">
                    Both use official APIs. Disconnect anytime from your portal.
                </p>
            </div>
        );
    }

    // At least one source connected → show connected state + option to add
    // the other one if the owner uses both.
    return (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="owner-data-connected-card">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-emerald-800" data-testid="owner-data-label">
                <CheckCircle2 className="h-4 w-4" /> {label}
            </div>

            {xeroOn && (
                <ul className="mt-2 space-y-1 text-sm text-[#0F1115]" data-testid="owner-xero-connections">
                    {xero.connections.map((c) => (
                        <li key={c.id} className="flex items-center gap-2" data-testid={`owner-xero-connection-${c.id}`}>
                            <span className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Xero:</span>
                            <span className="font-medium">{c.xero_tenant_name}</span>
                            <button
                                type="button"
                                onClick={() => syncXero(c.id)}
                                disabled={busy === `sync-x-${c.id}`}
                                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2.5 py-0.5 text-[11px] text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
                                data-testid={`owner-xero-sync-${c.id}`}
                            >
                                {busy === `sync-x-${c.id}` ? <><Loader2 className="h-3 w-3 animate-spin" /> Syncing…</> : <><RefreshCw className="h-3 w-3" /> Sync now</>}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {prosaicOn && (
                <ul className="mt-2 space-y-1 text-sm text-[#0F1115]" data-testid="owner-prosaic-connections">
                    {prosaic.connections.map((c) => (
                        <li key={c.id} className="flex items-center gap-2" data-testid={`owner-prosaic-connection-${c.id}`}>
                            <span className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Prosaic:</span>
                            <span className="font-medium">{c.prosaic_entity_name || c.prosaic_entity_id}</span>
                        </li>
                    ))}
                </ul>
            )}

            {/* Nudge to add the OTHER source if only one is on. */}
            <div className="mt-3 flex flex-wrap gap-2">
                {!xeroOn && xero.configured && (
                    <button
                        type="button"
                        onClick={startXero}
                        disabled={busy === "xero"}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-3 py-1 text-[12px] text-[#0F1115] hover:bg-[#F5F4EF] disabled:opacity-60"
                        data-testid="owner-xero-add-btn"
                    >
                        {busy === "xero" ? <Loader2 className="h-3 w-3 animate-spin" /> : "+ Add Xero"}
                    </button>
                )}
                {!prosaicOn && (
                    <button
                        type="button"
                        onClick={() => setShowProsaicForm((v) => !v)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-3 py-1 text-[12px] text-[#0F1115] hover:bg-[#F5F4EF]"
                        data-testid="owner-prosaic-add-btn"
                    >
                        + Add Prosaic
                    </button>
                )}
                {prosaicOn && (
                    <button
                        type="button"
                        onClick={disconnectProsaic}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-3 py-1 text-[12px] text-[#5E636E] hover:bg-rose-50 hover:text-rose-700"
                        data-testid="owner-prosaic-disconnect-btn"
                    >
                        <X className="h-3 w-3" /> Disconnect Prosaic
                    </button>
                )}
            </div>

            {showProsaicForm && !prosaicOn && (
                <form onSubmit={connectProsaic} className="mt-4 rounded-xl bg-white border border-[#E4E4E7] p-4 space-y-3" data-testid="owner-prosaic-form">
                    <label className="block text-[12px] text-[#5E636E]">
                        Paste your Prosaic API key
                        <input
                            type="password"
                            autoComplete="off"
                            value={prosaicKey}
                            onChange={(e) => setProsaicKey(e.target.value)}
                            placeholder="prosaic_live_…"
                            className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-sm text-[#0F1115] focus:outline-none focus:ring-2 focus:ring-[#0E3B2E]/20"
                            data-testid="owner-prosaic-key-input"
                        />
                    </label>
                    <div className="flex items-center gap-2">
                        <button type="submit" disabled={busy === "prosaic" || prosaicKey.trim().length < 8} className="inline-flex items-center gap-1.5 rounded-full bg-[#0E3B2E] px-4 py-2 text-sm font-medium text-white hover:bg-[#0B1F17] disabled:opacity-50" data-testid="owner-prosaic-submit">
                            {busy === "prosaic" ? <><Loader2 className="h-4 w-4 animate-spin" /> Connecting…</> : "Save & connect"}
                        </button>
                        <button type="button" onClick={() => { setShowProsaicForm(false); setProsaicKey(""); }} className="text-[13px] text-[#5E636E] hover:text-[#0F1115]">
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
